import { DomSanitizer } from '@angular/platform-browser';
import { Injectable } from '@angular/core';

import { HcService } from './hc-service';
import { AuthorisationStateService } from './authorisation-state.service';
import { NotificationsService } from '@lib/notifications';

export enum AvailableEmbeddedAppFeatures {
  DigitalSignature,
  Cockpit,
  BeneficiariesDeactivation,
  Tetris,
  ExternalUpload,
}

export type EmbeddedFeatureQueryParams = Record<string, string | number | boolean>;

export interface IEmbeddedFeatureUrlOptions {
  shouldSanitize?: boolean;
}

const EmbeddedAppFeatureURLs: Record<AvailableEmbeddedAppFeatures, string> = {
  [AvailableEmbeddedAppFeatures.DigitalSignature]: 'digital-signature',
  [AvailableEmbeddedAppFeatures.Cockpit]: 'cockpit',
  [AvailableEmbeddedAppFeatures.BeneficiariesDeactivation]: 'beneficiaries-deactivation',
  [AvailableEmbeddedAppFeatures.Tetris]: 'tetris',
  [AvailableEmbeddedAppFeatures.ExternalUpload]: 'upload-external-files',
};

@Injectable({
  providedIn: 'root',
})
export class EmbeddedTcAppService {
  constructor(
    private readonly hcService: HcService,
    private readonly sanitizer: DomSanitizer,
    private readonly notificationService: NotificationsService,
    private readonly authorisationService: AuthorisationStateService,
  ) {}

  /**
   * Sets temporary cookies for authenticating the embedded application.
   * These cookies are valid for a short duration (15 seconds) to allow the embedded app to authenticate the user
   * and then expire automatically.
   */
  public setTemporaryCookiesForEmbeddedAuth(): void {
    const { token, refreshToken } = this.getUserCredentials();

    if (!token || !refreshToken) {
      this.notificationService.error({
        message: 'Unable to retrieve user credentials for embedded application authentication.',
      });
    }

    // Note: If you're running the app locally, use 'localhost' as the domain for the cookies.
    const appDomain = this.getAppDomain();
    if (!appDomain) {
      this.notificationService.error({
        message: 'Unable to determine application domain for setting authentication cookies.',
      });
    }

    document.cookie = `userToken=${token}; domain=${appDomain}; path=/; max-age=15; Secure; SameSite=Lax`;
    document.cookie = `userRefreshToken=${refreshToken}; domain=${appDomain}; path=/; max-age=15; Secure; SameSite=Lax`;
  }

  /**
   * Get the URL for the embedded page which will then return the URL to be used to load the React app.
   */
  public getPrivateEmbeddedFeatureUrl(embeddedAppFeature: AvailableEmbeddedAppFeatures): string {
    const featurePath = EmbeddedAppFeatureURLs[embeddedAppFeature];
    const reactDomainUrl = this.getReactDomainUrl();

    const url = `${reactDomainUrl}/integration/embedded/${featurePath}`;
    const sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

    return sanitizedUrl as string;
  }

  public getPublicEmbeddedFeatureUrl(
    embeddedAppFeature: AvailableEmbeddedAppFeatures,
    queryParams?: EmbeddedFeatureQueryParams,
    options: IEmbeddedFeatureUrlOptions = { shouldSanitize: true },
  ): string {
    const url = this.getPublicEmbeddedFeatureUrlString(embeddedAppFeature, queryParams);

    if (options.shouldSanitize === false) {
      return url;
    }

    const sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

    return sanitizedUrl as string;
  }

  /**
   * Gets the application domain by stripping the subdomain from the health center's domain.
   *
   * We must use the HC configuration based on the domain to avoid issues
   * e.g. user connected in the HC XYZ but using the domain of HC ABC.
   */
  private getAppDomain(): string {
    const domain = this.hcService.getHcConfigByDomain().config.domain;
    const domainParts = domain.split('.');

    return domainParts.slice(1).join('.');
  }

  /**
   * Gets the React domain URL from the health center configuration.
   *
   * We must use the HC configuration based on the domain to avoid issues
   * e.g. user connected in the HC XYZ but using the domain of HC ABC.
   */
  private getReactDomainUrl(): string {
    const reactDomainKey = this.hcService.getHcConfigByDomain().config.reactDomainKey;
    const reactDomainUrl = this.hcService.getHcReactDomain(reactDomainKey);

    return reactDomainUrl;
  }

  private getPublicEmbeddedFeatureUrlString(
    embeddedAppFeature: AvailableEmbeddedAppFeatures,
    queryParams?: EmbeddedFeatureQueryParams,
  ): string {
    const featurePath = EmbeddedAppFeatureURLs[embeddedAppFeature];
    const reactDomainUrl = this.getReactDomainUrl();

    const url = new URL(`${reactDomainUrl}/integration/${featurePath}`);

    Object.entries(queryParams || {}).forEach(([key, value]) => {
      url.searchParams.set(key, String(value));
    });

    return url.toString();
  }

  private getUserCredentials(): { token: string | null; refreshToken: string | null } {
    const token = this.authorisationService.getToken() || '';
    const refreshToken = this.authorisationService.getRefreshToken() || '';

    return { token, refreshToken };
  }
}
